import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useListPipelineRunsQuery } from '@/services/api';
import { hasCapability, useProjectScope } from '@/context/ProjectContext';
import { RunCard } from '@/components/RunCard';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { useEventStream } from '@/hooks/useEventStream';
import type { PipelineRunListItemDto } from '@/types/api';

export function DashboardPage(): JSX.Element {
  const { project, user } = useProjectScope();
  const canSubmit = hasCapability(user, 'pipeline.create');
  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Pipeline dashboard</h1>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className="live-pill">
            <span className="dot" />
            Live · auto-refresh
          </span>
          <Link className="btn ghost sm" to={`/p/${project.slug}/runs`}>
            Run history →
          </Link>
          {canSubmit && (
            <Link className="btn primary sm" to={`/p/${project.slug}/new-run`} data-testid="new-run-action">
              + New run
            </Link>
          )}
        </div>
      </div>
      <div className="buckets">
        <Bucket
          title="Active"
          status="active"
          projectId={project.project_id}
          countLabel={(n) => String(n)}
          skeletonRows={3}
        />
        <Bucket
          title="Completed"
          status="completed"
          projectId={project.project_id}
          countLabel={() => 'last 10'}
          perPage={10}
        />
        <Bucket
          title="Failed"
          status="failed"
          projectId={project.project_id}
          countLabel={() => 'last 10'}
          perPage={10}
        />
      </div>
    </>
  );
}

function Bucket({
  title,
  status,
  projectId,
  countLabel,
  perPage = 50,
  skeletonRows
}: {
  title: string;
  status: 'active' | 'completed' | 'failed';
  projectId: string;
  countLabel: (n: number) => string;
  perPage?: number;
  skeletonRows?: number;
}): JSX.Element {
  const { data, isLoading, isError, refetch } = useListPipelineRunsQuery({
    status,
    project_id: projectId,
    per_page: perPage,
    sort: 'started_at_desc'
  });

  useEffect(() => {
    return () => {
    };
  }, []);

  return (
    <section className="bucket" aria-label={`${title} runs bucket`} data-testid={`bucket-${status}`}>
      <header className="bucket-head">
        <div className="bucket-title">{title}</div>
        <div className="bucket-count">
          {isLoading ? '…' : isError ? '—' : countLabel(data?.items.length ?? 0)}
        </div>
      </header>
      <div style={{ flex: 1 }}>
        {isLoading ? (
          <RunCardSkeleton rows={skeletonRows ?? perPage} />
        ) : isError ? (
          <ErrorState
            title={`Failed to load ${title.toLowerCase()} runs`}
            body="The pipeline-run service is unreachable."
            onRetry={() => void refetch()}
          />
        ) : data && data.items.length > 0 ? (
          <>
            {data.items.map((run) => (
              <RunCardWithStream
                key={run.id}
                run={run}
                listen={status === 'active'}
                onLiveFrame={status === 'active' ? () => void refetch() : undefined}
              />
            ))}
          </>
        ) : (
          <EmptyState
            compact
            icon={status === 'active' ? '◌' : status === 'completed' ? '✓' : '!'}
            title={emptyTitle(status)}
            body={emptyBody(status)}
          />
        )}
      </div>
    </section>
  );
}

function RunCardSkeleton({ rows }: { rows: number }): JSX.Element {
  const bar = (width: string, height = 10): React.CSSProperties => ({
    width,
    height,
    borderRadius: 3,
    background: 'var(--surface-sunken)'
  });
  return (
    <div role="status" aria-busy="true" aria-label="Loading runs">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)' }}
        >
          <div className="animate-skel" style={{ ...bar(`${58 + (i % 3) * 12}%`, 12), marginBottom: 4 }} />
          <div className="animate-skel" style={{ ...bar('34%'), marginBottom: 4 }} />
          <div className="animate-skel" style={{ ...bar('46%'), marginBottom: 5 }} />
          <div className="animate-skel" style={bar(`${40 + (i % 2) * 14}%`)} />
        </div>
      ))}
    </div>
  );
}

function RunCardWithStream({
  run,
  listen,
  onLiveFrame
}: {
  run: PipelineRunListItemDto;
  listen: boolean;
  onLiveFrame?: () => void;
}): JSX.Element {
  useEventStream(listen ? run.id : null, listen, () => {
    onLiveFrame?.();
  });
  return <RunCard run={run} />;
}

function emptyTitle(status: 'active' | 'completed' | 'failed'): string {
  if (status === 'active') return 'No active runs';
  if (status === 'completed') return 'No completed runs yet';
  return 'No failed runs';
}

function emptyBody(status: 'active' | 'completed' | 'failed'): string {
  if (status === 'active') return 'Active runs will appear here in real time as they start.';
  if (status === 'completed') return 'Successful runs will be listed here.';
  return 'Failures from the recent window will appear here.';
}
